/**
 * Memory guard (issue walk-bake#11, C9). iOS kills a page that uses too much memory without
 * giving it a chance to report an error, so the Player (1) shows the walk's graphics-memory
 * estimate, (2) warns before opening a walk that is over the device limit, and (3) remembers
 * that a walk started loading, so the next start can say what most likely happened.
 *
 * Pure functions: everything the browser provides is passed in, so they run under node:test.
 */

/** Graphics-memory limits above which the Player warns before opening, in MB. One constant each. */
export const GPU_LIMIT_MB_APPLE = 700;
export const GPU_LIMIT_MB_OTHER = 1500;

/** iOS/iPadOS, including an iPad that reports itself as a Mac in desktop mode. */
export function isAppleMobile(userAgent: string, maxTouchPoints: number): boolean {
  if (/iPad|iPhone|iPod/.test(userAgent)) return true;
  return /Macintosh/.test(userAgent) && maxTouchPoints > 1;
}

export function deviceGpuLimitMb(userAgent: string, maxTouchPoints: number): number {
  return isAppleMobile(userAgent, maxTouchPoints) ? GPU_LIMIT_MB_APPLE : GPU_LIMIT_MB_OTHER;
}

/** `1300` → "1.3 GB", `640` → "640 MB". */
export function formatGpuMb(mb: number): string {
  return mb >= 1000 ? `${(mb / 1000).toFixed(1)} GB` : `${Math.round(mb)} MB`;
}

/** "≈ 1.3 GB GPU", or "" when the walk has no estimate. */
export function gpuLabel(gpuMb: number | undefined): string {
  return gpuMb === undefined ? "" : `≈ ${formatGpuMb(gpuMb)} GPU`;
}

function positiveNumber(value: unknown): number | undefined {
  return typeof value === "number" && Number.isFinite(value) && value > 0 ? value : undefined;
}

/**
 * The estimate of a walk: `walk.json` `gpu_mb`; for older walks `export_report.json`
 * `gpu.total_mb`; otherwise nothing.
 */
export function gpuEstimateMb(walkJson: unknown, exportReport: unknown): number | undefined {
  const fromWalk = positiveNumber((walkJson as { gpu_mb?: unknown } | null | undefined)?.gpu_mb);
  if (fromWalk !== undefined) return fromWalk;
  return positiveNumber((exportReport as { gpu?: { total_mb?: unknown } } | null | undefined)?.gpu?.total_mb);
}

/** The optional `walk.json` `gpu_mb_uncompressed`: the estimate from the original files. */
export function gpuUncompressedMb(walkJson: unknown): number | undefined {
  return positiveNumber((walkJson as { gpu_mb_uncompressed?: unknown } | null | undefined)?.gpu_mb_uncompressed);
}

/** Which ASTC profiles the device decodes (`WEBGL_compressed_texture_astc.getSupportedProfiles()`). */
export interface AstcSupport {
  ldr: boolean;
  hdr: boolean;
}

/** No extension: no ASTC. An extension without `getSupportedProfiles`: LDR only (the baseline). */
export function astcSupportFromExtension(
  extension: { getSupportedProfiles?: () => string[] } | null | undefined,
): AstcSupport {
  if (!extension) return { ldr: false, hdr: false };
  let profiles: string[];
  try {
    profiles = extension.getSupportedProfiles?.() ?? ["ldr"];
  } catch {
    profiles = ["ldr"];
  }
  return { ldr: profiles.includes("ldr"), hdr: profiles.includes("hdr") };
}

/**
 * Whether the viewer loads a walk's ASTC copies (`compressed.json`): the lightmaps, the bulk of
 * the memory, need ASTC HDR; the textures need LDR (which every HDR device also has).
 */
export function astcUsable(support: AstcSupport): boolean {
  return support.hdr && support.ldr;
}

/** The Player URL's A/B switches that decide which copy of the textures the viewer loads. */
export interface FormatOverrides {
  lmFormat?: string | null;
  texFormat?: string | null;
}

/** `?lm_format=` and `?tex_format=` of the Player's own URL (the Player passes both to the viewer). */
export function formatOverridesFromSearch(search: string): FormatOverrides {
  const params = new URLSearchParams(search);
  return { lmFormat: params.get("lm_format"), texFormat: params.get("tex_format") };
}

/**
 * Whether the viewer will load the ASTC copies of a compressed walk: the device needs ASTC HDR
 * and LDR, and neither `?lm_format=rgb9e5|half` nor `?tex_format=orig` may ask for the originals.
 * (The viewer ignores other values: `auto`, `astc` and unknown ones all mean "ASTC if possible".)
 */
export function loadsAstc(support: AstcSupport, overrides: FormatOverrides = {}): boolean {
  if (!astcUsable(support)) return false;
  if (overrides.lmFormat === "rgb9e5" || overrides.lmFormat === "half") return false;
  return overrides.texFormat !== "orig";
}

/**
 * The estimate to warn with and to show while loading. With `compressed.json` a walk's `gpu_mb` is
 * the ASTC path and `gpu_mb_uncompressed` the figure for the original files. A device with ASTC
 * HDR loads the ASTC copies (`gpu_mb`); any other device, and any URL switch that asks for the
 * originals (even only for the lightmaps or only for the textures: the larger figure is the safe
 * one), loads the originals (`gpu_mb_uncompressed`, when present).
 */
export function effectiveGpuMb(
  gpuMb: number | undefined,
  gpuMbUncompressed: number | undefined,
  support: AstcSupport,
  overrides: FormatOverrides = {},
): number | undefined {
  return loadsAstc(support, overrides) ? gpuMb : (gpuMbUncompressed ?? gpuMb);
}

let probed: AstcSupport | undefined;

/** One tiny offscreen WebGL2 context, once per page load. No WebGL2 or no extension: no ASTC. */
export function probeAstcSupport(): AstcSupport {
  if (probed) return probed;
  let support: AstcSupport = { ldr: false, hdr: false };
  try {
    const gl = document.createElement("canvas").getContext("webgl2");
    if (gl) {
      support = astcSupportFromExtension(gl.getExtension("WEBGL_compressed_texture_astc"));
      gl.getExtension("WEBGL_lose_context")?.loseContext();
    }
  } catch {
    // Probe failed: treated as no ASTC, which warns with the larger (uncompressed) figure.
  }
  probed = support;
  return support;
}

/** Whether to ask before opening. A walk without an estimate never asks. */
export function needsMemoryWarning(gpuMb: number | undefined, limitMb: number): boolean {
  return gpuMb !== undefined && gpuMb > limitMb;
}

export function memoryWarningText(gpuMb: number): string {
  return `This walk needs about ${Math.round(gpuMb)} MB of graphics memory; this device may close it. Open anyway?`;
}

export interface LoadingFlag {
  id: string;
  name: string;
  gpuMb?: number;
  at: number;
}

const FLAG_KEY = "walk-player.loading";

type FlagStorage = Pick<Storage, "getItem" | "setItem" | "removeItem">;

/** Set when a walk starts loading. */
export function markLoading(storage: FlagStorage, flag: LoadingFlag): void {
  try {
    storage.setItem(FLAG_KEY, JSON.stringify(flag));
  } catch {
    // Storage blocked: no message next time, nothing else lost.
  }
}

/** Cleared when the viewer reports ready or errors, or the walk is closed normally. */
export function clearLoading(storage: FlagStorage): void {
  try {
    storage.removeItem(FLAG_KEY);
  } catch {
    // See markLoading.
  }
}

/** The flag left by a walk that never finished loading, removed so it is shown only once. */
export function takeInterruptedLoad(storage: FlagStorage): LoadingFlag | null {
  try {
    const raw = storage.getItem(FLAG_KEY);
    if (!raw) return null;
    storage.removeItem(FLAG_KEY);
    const flag = JSON.parse(raw) as Partial<LoadingFlag>;
    if (typeof flag.id !== "string" || typeof flag.name !== "string") return null;
    return { id: flag.id, name: flag.name, gpuMb: positiveNumber(flag.gpuMb), at: Number(flag.at) || 0 };
  } catch {
    return null;
  }
}

/** The estimate is this share of the device limit or more: the memory explanation is plausible. */
export const MEMORY_SUSPECT_SHARE = 0.6;

/**
 * What to tell the user about a walk that never finished loading. The flag cannot say why: the
 * page may have been closed or reloaded, or killed for memory. Only an estimate near the
 * device limit (`limitMb`, from `deviceGpuLimitMb`) makes "ran out of memory" the likely cause.
 */
export function interruptedLoadText(flag: LoadingFlag, limitMb: number): string {
  const estimate = flag.gpuMb === undefined ? "" : ` (estimate ${formatGpuMb(flag.gpuMb)})`;
  if (flag.gpuMb !== undefined && flag.gpuMb >= MEMORY_SUSPECT_SHARE * limitMb) {
    return (
      `The last walk (${flag.name}) closed while loading${estimate} — most likely it ran out of memory on this device. ` +
      "Export a lighter version (Phone preset or a lower GPU budget in Walk Bake)."
    );
  }
  return (
    `The last walk (${flag.name}) did not finish loading${estimate} — the page was closed or reloaded, or the device ran out of memory. ` +
    "Open it again; if it keeps closing, export a lighter version (Phone preset or a lower GPU budget in Walk Bake)."
  );
}
